import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { translatePaymentType, getPaymentTypeColor } from '../../../utils/paymentTypeTranslator';

dayjs.locale('it');

const sortByDateDesc = (rows) =>
  [...rows].sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf());

const formatTypeLabel = (type) =>
  (type || '')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

const getTypeColor = (type) => {
  switch (type) {
    case 'nuovo corso':
      return 'success';
    case 'rinnovo corso':
      return 'warning';
    case 'Spesa corso':
      return 'error';
    case 'Compenso lezioni':
      return 'warning';
    case 'Altro rimborso':
      return 'error';
    default:
      return 'default';
  }
};

const formatMoney = (amount) =>
  `${amount.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;

const DetailTable = ({ title, rows, emptyLabel }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
      {title}
    </Typography>
    {rows.length === 0 ? (
      <Typography variant='body2' color='text.secondary'>
        {emptyLabel}
      </Typography>
    ) : (
      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Descrizione</TableCell>
              <TableCell align='right'>Importo</TableCell>
              <TableCell>Metodo</TableCell>
              <TableCell>Utente</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const user = row.user;
              const fullName = user ? `${user.name || ''} ${user.surname || ''}`.trim() : '';
              return (
                <TableRow key={String(row.id)}>
                  <TableCell>{dayjs(row.createdAt).format('DD/MM/YYYY')}</TableCell>
                  <TableCell>
                    <Chip label={formatTypeLabel(row.type)} color={getTypeColor(row.type)} size='small' />
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>{row.name || '—'}</Typography>
                    {row.note ? (
                      <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>
                        {row.note}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align='right'>
                    <Typography
                      variant='body2'
                      fontWeight={500}
                      color={row.amount >= 0 ? 'success.main' : 'error.main'}
                    >
                      {formatMoney(row.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.method ? (
                      <Chip
                        label={translatePaymentType(row.method)}
                        color={getPaymentTypeColor(row.method)}
                        size='small'
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    {fullName ? (
                      <Box>
                        <Typography variant='body2'>{fullName}</Typography>
                        {user?.email ? (
                          <Typography variant='caption' color='text.secondary'>
                            {user.email}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Box>
);

const ModalFinanceCourseMonthDetails = ({ open, onClose, monthLabel, courseLabel, row }) => {
  const { income, expense, teacher, other } = useMemo(() => {
    const r = row || {};
    return {
      income: sortByDateDesc(r.incomeLines || []),
      expense: sortByDateDesc(r.expenseLines || []),
      teacher: sortByDateDesc(r.teacherCompensationLines || []),
      other: sortByDateDesc(r.otherCompensationLines || []),
    };
  }, [row]);

  const titleCourse = courseLabel || 'Corso';

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        Dettaglio bilancio corso — {titleCourse}
        {monthLabel ? ` — ${monthLabel}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        <DetailTable title='Entrate' rows={income} emptyLabel='Nessuna entrata in questo mese.' />
        <DetailTable title='Spese per corso' rows={expense} emptyLabel='Nessuna spesa per corso in questo mese.' />
        <DetailTable
          title='Rimborsi docente (lezioni)'
          rows={teacher}
          emptyLabel='Nessun compenso lezioni in questo mese.'
        />
        <DetailTable
          title='Altri rimborsi docente'
          rows={other}
          emptyLabel='Nessun altro rimborso in questo mese.'
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant='contained' color='primary'>
          Chiudi
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalFinanceCourseMonthDetails;
